import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, Alert, Modal, ProgressBar, Tab, Tabs, Spinner } from 'react-bootstrap';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { getProtectedFileUrl, getStoragePathFromUrl } from '../services/protectedFileService';

const ContentViewer = ({ moduleId, contentId, onProgressUpdate }) => {
  const { currentUser } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingProgress, setViewingProgress] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [userProgress, setUserProgress] = useState({});
  const [showNotes, setShowNotes] = useState(false);
  const [personalNotes, setPersonalNotes] = useState('');
  const [protectedFileUrl, setProtectedFileUrl] = useState('');
  const [fileAccessError, setFileAccessError] = useState('');
  const videoRef = useRef(null);
  const [videoStats, setVideoStats] = useState({
    currentTime: 0,
    duration: 0,
    watchedPercentage: 0
  });

  useEffect(() => {
    loadContent();
    loadUserProgress();
  }, [moduleId, contentId]);

  useEffect(() => {
    let active = true;

    const loadProtectedFileUrl = async () => {
      const path = content?.file?.path || getStoragePathFromUrl(content?.file?.url);
      if (!path) {
        setProtectedFileUrl('');
        setFileAccessError('This file needs to be re-uploaded before it can be opened securely.');
        return;
      }

      try {
        setFileAccessError('');
        const url = await getProtectedFileUrl({ path, moduleId });
        if (active) setProtectedFileUrl(url);
      } catch (error) {
        if (active) {
          setProtectedFileUrl('');
          setFileAccessError(error.message);
        }
      }
    };

    if (content) loadProtectedFileUrl();
    return () => {
      active = false;
    };
  }, [content, moduleId]);

  const loadContent = async () => {
    try {
      setLoading(true);
      const moduleDoc = await getDoc(doc(db, 'modules', moduleId));
      
      if (moduleDoc.exists()) {
        const moduleData = moduleDoc.data();
        const contentItem = moduleData.content?.find(c => c.id === contentId);
        
        if (contentItem) {
          setContent(contentItem);
          
          // Check if content should be accessible based on access level
          if (!checkContentAccess(contentItem)) {
            throw new Error('You do not have access to this content');
          }
        } else {
          throw new Error('Content not found');
        }
      } else {
        throw new Error('Module not found');
      }
    } catch (error) {
      devError('Error loading content:', error);
      alert(`Error loading content: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const progressData = userData.contentProgress || {};
        const contentProgress = progressData[contentId] || {};
        
        setUserProgress(contentProgress);
        setPersonalNotes(contentProgress.notes || '');
        
        if (contentProgress.watchedPercentage) {
          setViewingProgress(contentProgress.watchedPercentage);
        }
      }
    } catch (error) {
      devError('Error loading user progress:', error);
    }
  };

  const checkContentAccess = (contentItem) => {
    // Implement access level checking logic
    // For now, return true - in production, check user's enrollment, cohort, etc.
    return true;
  };

  const updateProgress = async (percentage, additionalData = {}) => {
    if (!currentUser || !content) return;

    try {
      const progressUpdate = {
        contentId: content.id,
        moduleId: moduleId,
        percentage: Math.round(percentage),
        lastViewed: new Date().toISOString(),
        completed: percentage >= 90, // Mark as completed when 90% viewed
        ...additionalData
      };

      // Update user's progress in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        [`contentProgress.${content.id}`]: progressUpdate,
        lastActivity: new Date().toISOString()
      });

      setUserProgress(progressUpdate);
      
      if (onProgressUpdate) {
        onProgressUpdate(progressUpdate);
      }
    } catch (error) {
      devError('Error updating progress:', error);
    }
  };

  const savePersonalNotes = async () => {
    if (!currentUser || !content) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        [`contentProgress.${content.id}.notes`]: personalNotes,
        [`contentProgress.${content.id}.notesUpdated`]: new Date().toISOString()
      });

      setUserProgress(prev => ({
        ...prev,
        notes: personalNotes,
        notesUpdated: new Date().toISOString()
      }));

      alert('Notes saved successfully!');
    } catch (error) {
      devError('Error saving notes:', error);
      alert('Failed to save notes');
    }
  };

  const handleVideoProgress = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const currentTime = video.currentTime;
    const duration = video.duration;
    const watchedPercentage = (currentTime / duration) * 100;

    setVideoStats({
      currentTime,
      duration,
      watchedPercentage
    });

    // Update progress every 10% increment
    if (watchedPercentage > viewingProgress + 10) {
      setViewingProgress(watchedPercentage);
      updateProgress(watchedPercentage, {
        currentTime,
        duration,
        watchTime: currentTime
      });
    }
  };

  const handleVideoComplete = () => {
    updateProgress(100, {
      completed: true,
      completedAt: new Date().toISOString(),
      totalWatchTime: videoStats.duration
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('video')) return '🎥';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('audio')) return '🎵';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('document') || fileType?.includes('word')) return '📝';
    if (fileType?.includes('presentation') || fileType?.includes('powerpoint')) return '📊';
    return '📁';
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderContent = () => {
    if (!content) return null;

    const fileType = content.file.type;
    const fileUrl = protectedFileUrl;

    if (fileAccessError) {
      return <Alert variant="danger">{fileAccessError}</Alert>;
    }

    if (!fileUrl) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Preparing secure file access...
        </div>
      );
    }

    if (fileType.startsWith('video/')) {
      return (
        <div className="video-container">
          <video
            ref={videoRef}
            width="100%"
            height="auto"
            controls
            onTimeUpdate={handleVideoProgress}
            onEnded={handleVideoComplete}
            onLoadedMetadata={() => {
              if (userProgress.currentTime && videoRef.current) {
                videoRef.current.currentTime = userProgress.currentTime;
              }
            }}
          >
            <source src={fileUrl} type={fileType} />
            Your browser does not support the video tag.
          </video>
          
          {videoStats.duration > 0 && (
            <div className="video-info mt-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">
                  {formatDuration(videoStats.currentTime)} / {formatDuration(videoStats.duration)}
                </small>
                <small className="text-muted">
                  {Math.round(videoStats.watchedPercentage)}% watched
                </small>
              </div>
              <ProgressBar 
                now={videoStats.watchedPercentage} 
                variant={videoStats.watchedPercentage > 90 ? 'success' : 'primary'}
                style={{ height: '4px' }}
              />
            </div>
          )}
        </div>
      );
    }

    if (fileType.startsWith('image/')) {
      return (
        <div className="image-container text-center">
          <img
            src={fileUrl}
            alt={content.title}
            style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
            onLoad={() => updateProgress(100)}
          />
        </div>
      );
    }

    if (fileType.startsWith('audio/')) {
      return (
        <div className="audio-container">
          <audio
            width="100%"
            controls
            onLoadedMetadata={() => updateProgress(10)}
            onEnded={() => updateProgress(100)}
          >
            <source src={fileUrl} type={fileType} />
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    if (fileType === 'application/pdf' || fileType.includes('document')) {
      return (
        <div className="document-container">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6>{getFileIcon(fileType)} {content.file.filename}</h6>
              <small className="text-muted">
                Size: {(content.file.size / (1024 * 1024)).toFixed(2)} MB
              </small>
            </div>
            <div>
              <Button
                variant="primary"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => updateProgress(50)}
              >
                📥 Download
              </Button>
              <Button
                variant="outline-primary"
                className="ms-2"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => updateProgress(100)}
              >
                👁️ View
              </Button>
            </div>
          </div>
          
          {fileType === 'application/pdf' && (
            <div className="pdf-embed">
              <iframe
                src={`${fileUrl}#toolbar=1`}
                width="100%"
                height="600px"
                title={content.title}
                onLoad={() => updateProgress(80)}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="generic-file-container">
        <div className="text-center py-5">
          <div className="mb-3" style={{ fontSize: '3rem' }}>
            {getFileIcon(fileType)}
          </div>
          <h5>{content.file.filename}</h5>
          <p className="text-muted">
            {content.description || 'Click download to access this content'}
          </p>
          <Button
            variant="primary"
            size="lg"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => updateProgress(100)}
          >
            📥 Download Content
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading content...</span>
        </Spinner>
      </div>
    );
  }

  if (!content) {
    return (
      <Alert variant="warning">
        <h6>Content Not Found</h6>
        <p>The requested content could not be loaded. It may have been moved or deleted.</p>
      </Alert>
    );
  }

  return (
    <div className="content-viewer">
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">{getFileIcon(content.file.type)} {content.title}</h5>
              <div className="d-flex gap-2 align-items-center">
                <Badge bg="primary">{content.type}</Badge>
                <Badge bg="secondary">{content.category}</Badge>
                {content.metadata.duration && (
                  <Badge bg="info">⏱️ {content.metadata.duration}</Badge>
                )}
                {userProgress.completed && (
                  <Badge bg="success">✅ Completed</Badge>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowNotes(!showNotes)}
              >
                📝 Notes
              </Button>
              {content.file.type.startsWith('video/') && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowFullscreen(true)}
                >
                  ⛶ Fullscreen
                </Button>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {content.description && (
            <Alert variant="info" className="mb-3">
              {content.description}
            </Alert>
          )}

          <Tabs defaultActiveKey="content" id="content-viewer-tabs">
            <Tab eventKey="content" title="📖 Content">
              {renderContent()}
            </Tab>
            
            {showNotes && (
              <Tab eventKey="notes" title="📝 My Notes">
                <div className="notes-section">
                  <div className="mb-3">
                    <label className="form-label">Personal Notes</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={personalNotes}
                      onChange={(e) => setPersonalNotes(e.target.value)}
                      placeholder="Take notes about this content..."
                    />
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {userProgress.notesUpdated && 
                        `Last saved: ${new Date(userProgress.notesUpdated).toLocaleString()}`
                      }
                    </small>
                    <Button variant="primary" onClick={savePersonalNotes}>
                      💾 Save Notes
                    </Button>
                  </div>
                </div>
              </Tab>
            )}
          </Tabs>

          {/* Content Metadata */}
          <div className="content-metadata mt-4">
            <div className="row">
              <div className="col-md-6">
                <h6>Content Information</h6>
                <ul className="list-unstyled small">
                  <li><strong>Type:</strong> {content.type}</li>
                  <li><strong>Category:</strong> {content.category}</li>
                  <li><strong>Access Level:</strong> {content.accessLevel}</li>
                  <li><strong>File Size:</strong> {(content.file.size / (1024 * 1024)).toFixed(2)} MB</li>
                </ul>
              </div>
              <div className="col-md-6">
                <h6>Your Progress</h6>
                <div className="progress-info">
                  <ProgressBar 
                    now={userProgress.percentage || 0} 
                    label={`${userProgress.percentage || 0}%`}
                    variant={userProgress.completed ? 'success' : 'primary'}
                  />
                  <div className="small text-muted mt-1">
                    {userProgress.lastViewed && (
                      <div>Last viewed: {new Date(userProgress.lastViewed).toLocaleString()}</div>
                    )}
                    {userProgress.completed && (
                      <div className="text-success">✅ Completed on {new Date(userProgress.completedAt).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          {content.metadata.tags && content.metadata.tags.length > 0 && (
            <div className="content-tags mt-3">
              <small className="text-muted">Tags: </small>
              {content.metadata.tags.map((tag, index) => (
                <Badge key={index} bg="light" text="dark" className="me-1">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Fullscreen Modal for Videos */}
      <Modal 
        show={showFullscreen} 
        onHide={() => setShowFullscreen(false)} 
        size="xl" 
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>{content.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {content.file.type.startsWith('video/') && (
            <video
              width="100%"
              height="100%"
              controls
              autoPlay
              style={{ backgroundColor: '#000' }}
            >
              <source src={protectedFileUrl} type={content.file.type} />
              Your browser does not support the video tag.
            </video>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ContentViewer;
